<template>
  <div ref="root" class="ak-modal-container">
    <transition
      name="modal-fade"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @enter="onEnter"
    >
      <div
        v-if="isActive"
        v-trap-focus="trapFocus"
        tabindex="-1"
        :role="ariaRole"
        :aria-modal="ariaModal"
        class="ak-modal"
        :class="{ 'ak-modal--outer-scroll': outerScroll }"
        :style="zIndex"
        @click.self="cancel('outside')"
      >
        <transition :name="animation">
          <ak-form
            v-show="showContent"
            class="ak-modal__dialog"
            :class="dialogClasses"
            @submit="submitHandler($event, props && props.modelValue)"
          >
            <ak-close
              v-if="showX"
              class="ak-modal__close"
              @click.prevent="cancel('x')"
            />

            <component :is="component" v-if="component" v-bind="props" />

            <ak-modal-body v-else-if="content">
              <div v-html="content" />
            </ak-modal-body>

            <slot v-else :close="close" />
          </ak-form>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref } from 'vue'
import { modalProps, modalMixin } from '../../mixins/modalMixin'
import Form from '../form/Form.vue'
import Icon from '../icon/Icon.vue'
import Close from '../close/Close.vue'
import trapFocus from '../../directives/trapFocus'
import ModalBody from './ModalBody.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-modal></ak-modal>
   */
  name: 'AkModal',

  /**
   * @type {Object}
   * @description A collection of directives that this component is
   * dependant on.
   */
  directives: {
    trapFocus,
  },

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Icon.name]: Icon,
    [Form.name]: Form,
    [Close.name]: Close,
    [ModalBody.name]: ModalBody,
  },

  /**
   * @return Object
   * @description Provides the returned object to the components decendants.
   */
  provide() {
    return {
      akModal: this,
    }
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...modalProps,
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['close', 'update:active'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes object for the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit, attrs }) {
    const root = ref(null)

    const mixin = modalMixin({
      type: 'modal',
      props,
      emit,
      attrs,
      refs: {
        root,
      },
    })

    return {
      ...mixin,
      root,
      attrs,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
$modal-default: 600px;
$modal-tiny: 425px;
$modal-small: 500px;
$modal-big: 750px;
$modal-large: 900px;
$modal-huge: 1200px;

$modal-shadow: 0 2.8px 2.2px rgba(0, 0, 0, 0.02),
  0 6.7px 5.3px rgba(0, 0, 0, 0.028), 0 12.5px 10px rgba(0, 0, 0, 0.035),
  0 22.3px 17.9px rgba(0, 0, 0, 0.042), 0 41.8px 33.4px rgba(0, 0, 0, 0.05),
  0 100px 80px rgba(0, 0, 0, 0.07);

.ak-modal {
  $self: &;
  position: fixed;
  top: 0;
  right: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  outline: 0;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: rgba(0, 0, 0, 0.5);
  padding-bottom: $spacing;
  padding-top: $spacing;

  &--outer-scroll {
    overflow-y: auto;
  }

  &--drawer {
    background-color: transparent;
    transition: background-color $transition-time ease-out;
    padding-top: 0;
    padding-bottom: 0;
    z-index: 98;
  }

  &--inset {
    &-right {
      padding-right: $spacing;
    }
    &-left {
      padding-left: $spacing;
    }
  }
  &--has-backdrop {
    background-color: rgba(0, 0, 0, 0.5);
  }

  &--right {
    align-items: flex-start;
    justify-content: flex-end;
  }

  &--left {
    align-items: flex-start;
    justify-content: flex-start;
    right: auto;
    left: 0;
  }

  &--has-container {
    height: 100%;
    width: 100%;
    position: absolute;
  }

  &__dialog {
    background-color: #fff;
    border-radius: $base-border-radius;
    width: 100%;
    max-width: $modal-default;
    max-height: 100%;
    box-shadow: $modal-shadow;
    display: flex;
    position: relative;
    flex-direction: column;
    overflow: hidden;
    transition: $transition-time max-width ease-out;

    &--outer-scroll {
      margin: auto;
      max-height: none;
      overflow: visible;
    }

    &--drawer {
      border-radius: 0;
      height: 100%;
    }

    &--tiny {
      max-width: $modal-tiny;
    }
    &--small {
      max-width: $modal-small;
    }
    &--big {
      max-width: $modal-big;
    }
    &--large {
      max-width: $modal-large;
    }
    &--huge {
      max-width: $modal-huge;
    }
  }

  &__close {
    margin-top: $spacing-small;
    margin-right: $spacing-small;
    position: absolute;
    top: 0;
    right: 0;
    z-index: 5;
    color: inherit;
  }

  &__body {
    padding: $spacing;
    overflow-y: auto;
    flex-grow: 1;

    &--paddingless {
      padding: 0;
    }

    &--outer-scroll {
      overflow-y: visible;
    }

    &--flex {
      display: flex;
      flex-direction: column;
    }
  }

  &__footer {
    flex-shrink: 0;
    display: flex;
    overflow: hidden;

    &-item {
      flex-grow: 1;
      flex-basis: 0;
      &.ak-btn,
      & {
        border-radius: 0 0 $base-border-radius $base-border-radius;
      }
      :first-child &,
      :first-child &.ak-btn {
        border-bottom-left-radius: 0;
      }
      :last-child &,
      :last-child &.ak-btn {
        border-bottom-right-radius: 0;
      }
    }

    // &-item + &-item {
    //   margin-left: 1px;
    // }
  }

  &__backdrop {
    background-color: rgba(0, 0, 0, 0.5);
  }
}

$fade-transition-time: 0.3s;
.modal-fade {
  &-enter,
  &-leave-to {
    opacity: 0;
  }

  &-enter-active,
  &-leave-active {
    transition: $fade-transition-time opacity ease-out;
  }

  &-leave-active {
    transition-duration: calc($fade-transition-time / 2);
  }
}
</style>
